import { testMediaConnectionBitrate } from '@twilio/rtc-diagnostics';

type GetTurnCredentialsFn = (token: string, wsServer: string) => Promise<RTCIceServer[]>;
type WsServerFn = (environment: string, region: string) => string;

interface TwilioPreflightInternals {
  getTurnCredentials: GetTurnCredentialsFn;
  WS_SERVER: WsServerFn;
}

let twilioPreflightInternalsPromise: Promise<TwilioPreflightInternals> | null = null;

const loadTwilioPreflightInternals = async (): Promise<TwilioPreflightInternals> => {
  if (!twilioPreflightInternalsPromise) {
    twilioPreflightInternalsPromise = Promise.all([
      import('twilio-video/es5/preflight/getturncredentials.js'),
      import('twilio-video/es5/util/constants.js'),
    ]).then(([turnCredentialsModule, constantsModule]) => {
      const getTurnCredentials = (turnCredentialsModule as { getTurnCredentials?: GetTurnCredentialsFn })
        .getTurnCredentials;
      const WS_SERVER = (constantsModule as { WS_SERVER?: WsServerFn }).WS_SERVER;

      if (typeof getTurnCredentials !== 'function' || typeof WS_SERVER !== 'function') {
        throw new Error('Unable to load Twilio preflight internals');
      }

      return { getTurnCredentials, WS_SERVER };
    });
  }
  return twilioPreflightInternalsPromise;
};

export interface BitrateTestSummary {
  averageBitrate: number;
  values: number[];
}

interface RunBitrateTestOptions {
  token: string;
  abort?: AbortSignal;
  durationMs?: number;
  minBitrateThreshold?: number;
}

const toAbortError = () => new DOMException('Aborted', 'AbortError');

export async function runBitrateTest(opts: RunBitrateTestOptions): Promise<BitrateTestSummary | null> {
  const { token, abort, durationMs = 15_000, minBitrateThreshold = 800 } = opts;
  if (abort?.aborted) throw toAbortError();

  try {
    const { WS_SERVER, getTurnCredentials } = await loadTwilioPreflightInternals();
    const wsServer = WS_SERVER('prod', 'gll');
    const iceServers = await getTurnCredentials(token, wsServer);
    if (!Array.isArray(iceServers) || iceServers.length === 0) return null;

    return await new Promise<BitrateTestSummary | null>((resolve, reject) => {
      let done = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let test: ReturnType<typeof testMediaConnectionBitrate> | null = null;

      const finish = (value: BitrateTestSummary | null) => {
        if (done) return;
        done = true;
        if (timeoutId) clearTimeout(timeoutId);
        abort?.removeEventListener('abort', onAbort);
        resolve(value);
      };

      const fail = (error: unknown) => {
        if (done) return;
        done = true;
        if (timeoutId) clearTimeout(timeoutId);
        abort?.removeEventListener('abort', onAbort);
        reject(error);
      };

      const onAbort = () => {
        try {
          test?.stop();
        } catch {}
        fail(toAbortError());
      };

      try {
        test = testMediaConnectionBitrate({ iceServers, minBitrateThreshold });
      } catch (error) {
        console.error('bitrate setup error:', error);
        finish(null);
        return;
      }

      abort?.addEventListener('abort', onAbort, { once: true });
      timeoutId = setTimeout(() => {
        try {
          test?.stop();
        } catch {}
      }, durationMs);

      test.on('error', error => {
        console.error('bitrate test error:', error);
      });

      test.on('end', report => {
        const averageBitrate =
          typeof report?.averageBitrate === 'number' && Number.isFinite(report.averageBitrate)
            ? report.averageBitrate
            : null;
        const values = Array.isArray(report?.values) ? report.values.filter((v: unknown) => typeof v === 'number') : [];

        if (averageBitrate == null) {
          finish(null);
          return;
        }

        finish({
          averageBitrate,
          values,
        });
      });
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    console.error('bitrate setup error:', error);
    return null;
  }
}
